import * as React from "react";
import { useState } from "react";
import { Box, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { useGetFieldAdminHSHistoryQuery, useExportFieldAdminHSPdfMutation } from "../fieldAdminApi";
import { CARD_BG, BORDER, AMBER } from "../../../Global/colors";
import { OK_GREEN_TEXT, WARN_YELLOW, FONT_FAMILY, teamLabel, parseLocalDate, formatTimeOfDay } from "../designTokens";
import { HSPageShell } from "./HSStepShell";
import { FATextField } from "../StockFormShell";
import { computeStepMeta, SHORT_NAMES } from "./WorkflowSummary";
import { PRESETS, last31DaysRange, toISODate } from "./dateRangePresets";
import { downloadHSExport } from "./hsExport";
import { FAEmptyState } from "../FALayout";

export default function History() {
  const navigate = useNavigate();
  const [defaultFrom, defaultTo] = last31DaysRange();
  const [fromDate, setFromDate] = useState(defaultFrom);
  const [toDate, setToDate] = useState(defaultTo);

  const { data, isSuccess } = useGetFieldAdminHSHistoryQuery({ date_from: fromDate, date_to: toDate });
  const [exportPdf] = useExportFieldAdminHSPdfMutation();
  const [exportError, setExportError] = useState(null);
  const todayISO = toISODate(new Date());

  const handleExportDay = async (date) => {
    setExportError(null);
    // include_attendance_signatures is the flag the backend actually reads; the earlier
    // include_attendance was ignored, so this row's export silently used the defaults.
    const message = await downloadHSExport(exportPdf, {
      date_from: date,
      date_to: date,
      include_daily_logs: true,
      include_attendance_signatures: true,
      include_fa_notes: true,
      include_photos: false,
    });
    setExportError(message);
  };

  const activePreset = PRESETS.find((p) => {
    const [from, to] = p.range();
    return from === fromDate && to === toDate;
  });

  const applyPreset = (preset) => {
    const [from, to] = preset.range();
    setFromDate(from);
    setToDate(to);
  };

  return (
    <HSPageShell>
      <Box sx={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: "16px", mb: "22px", flexWrap: "wrap" }}>
        <Box>
          <Typography sx={{ fontSize: "1.75rem", fontWeight: 700, color: "#fff", letterSpacing: "-0.025em" }}>
            H&S History
          </Typography>
          {/* "DoL-ready digital file · team · company" — the file is a company record as much
              as a team one, and the company is what a DoL inspector asks for first. */}
          <Typography sx={{ fontSize: "0.86rem", color: "rgba(255,255,255,0.55)", mt: "3px" }}>
            DoL-ready digital file
            {isSuccess && data.team_name ? ` · ${teamLabel(data.team_name, data.team_id)}` : ""}
            {isSuccess && data.installer_company_name ? ` · ${data.installer_company_name}` : ""}
          </Typography>
        </Box>
        <ExportButton onClick={() => navigate(`/field-admin/hs/export/?from=${fromDate}&to=${toDate}`)} />
      </Box>

      <Box sx={{ backgroundColor: CARD_BG, border: `1px solid ${BORDER}`, borderRadius: "12px", p: "16px", mb: "22px" }}>
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", mb: "12px" }}>
          <FATextField label="From" type="date" size="small" value={fromDate} onChange={(e) => setFromDate(e.target.value)} InputLabelProps={{ shrink: true }} />
          <FATextField label="To" type="date" size="small" value={toDate} onChange={(e) => setToDate(e.target.value)} InputLabelProps={{ shrink: true }} />
        </Box>
        <Box sx={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
          {PRESETS.map((preset) => (
            <Box
              key={preset.label}
              onClick={() => applyPreset(preset)}
              sx={{
                px: "12px",
                py: "6px",
                borderRadius: "999px",
                fontSize: "0.74rem",
                fontWeight: 600,
                cursor: "pointer",
                backgroundColor: activePreset?.label === preset.label ? "rgba(253,185,64,0.14)" : "rgba(255,255,255,0.04)",
                border: activePreset?.label === preset.label ? "1px solid rgba(253,185,64,0.35)" : "1px solid rgba(255,255,255,0.08)",
                color: activePreset?.label === preset.label ? AMBER : "rgba(255,255,255,0.7)",
              }}
            >
              {preset.label}
            </Box>
          ))}
        </Box>
      </Box>

      {exportError ? (
        <Typography sx={{ color: "#F87171", fontSize: "0.82rem", mb: "12px" }}>{exportError}</Typography>
      ) : null}

      {isSuccess && data.days.length ? (
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" }, gap: "10px", mb: "22px" }}>
          <HistoryTile
            label="Total records"
            value={data.days.length}
            sub={`${formatShort(fromDate)} – ${formatShort(toDate)}`}
          />
          {/* Records against calendar days in the range: "22 / 24" reads as coverage, which
              is the question a DoL file has to answer, where a bare count does not. */}
          <HistoryTile
            label="Days covered"
            value={data.days.length}
            suffix={` / ${daysInRange(fromDate, toDate)}`}
            sub={incompleteCount(data.days) ? `${incompleteCount(data.days)} incomplete · flagged` : "All days complete"}
          />
          <HistoryTile
            label="Last export"
            value={data.last_export ? formatShort(data.last_export.generated_at) : "—"}
            sub={data.last_export
              ? `${formatShort(data.last_export.date_from)} – ${formatShort(data.last_export.date_to)} range`
              : "Nothing exported yet"}
          />
        </Box>
      ) : null}

      {!isSuccess ? null : data.days.length === 0 ? (
        <FAEmptyState card py="70px">No H&S records in this range.</FAEmptyState>
      ) : (
        groupByWeek(data.days).map((group) => (
          <Box key={group.key} sx={{ mb: "18px" }}>
            {/* The design heads each week with its own count and incomplete tally. */}
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "10px", pt: "6px", pb: "10px" }}>
              <Typography sx={{ fontSize: "0.78rem", fontWeight: 600, color: "rgba(255,255,255,0.8)" }}>
                Week of {group.label}
              </Typography>
              <Typography sx={{ fontSize: "0.7rem", color: "rgba(255,255,255,0.5)" }}>
                {group.days.length} day{group.days.length === 1 ? "" : "s"}
                {group.incomplete ? ` · ${group.incomplete} incomplete` : ""}
              </Typography>
            </Box>
            <Box sx={{ backgroundColor: CARD_BG, border: `1px solid ${BORDER}`, borderRadius: "12px", overflow: "hidden" }}>
              {group.days.map((day, index) => (
                <DayRow
                  key={day.date}
                  day={day}
                  isLast={index === group.days.length - 1}
                  isToday={day.date === todayISO}
                  onContinue={() => navigate("/field-admin/hs/")}
                  onExportDay={() => handleExportDay(day.date)}
                />
              ))}
            </Box>
          </Box>
        ))
      )}

      {/* Sticky range export, matching the design's float CTA. */}
      {isSuccess && data.days.length ? (
        <Box sx={{ display: "flex", justifyContent: "center", mt: "4px" }}>
          <ExportButton
            wide
            label="Export range → PDF"
            onClick={() => navigate(`/field-admin/hs/export/?from=${fromDate}&to=${toDate}`)}
          />
        </Box>
      ) : null}
    </HSPageShell>
  );
}

// hs-history.html groups the list by week rather than presenting one flat run.
// .sum-tile — the three summary tiles above the week list.
function HistoryTile({ label, value, suffix, sub }) {
  return (
    <Box sx={{ backgroundColor: CARD_BG, border: `1px solid ${BORDER}`, borderRadius: "12px", px: "16px", py: "14px" }}>
      <Typography sx={{ fontSize: "0.62rem", fontWeight: 700, color: "rgba(255,255,255,0.45)", textTransform: "uppercase", letterSpacing: "0.07em", mb: "4px" }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: "1.5rem", fontWeight: 700, color: "#fff", lineHeight: 1.1, fontVariantNumeric: "tabular-nums" }}>
        {value}
        {suffix ? (
          <Box component="span" sx={{ fontSize: "0.8rem", fontWeight: 500, color: "rgba(255,255,255,0.4)" }}>{suffix}</Box>
        ) : null}
      </Typography>
      {sub ? (
        <Typography sx={{ fontSize: "0.7rem", color: "rgba(255,255,255,0.45)", mt: "2px" }}>{sub}</Typography>
      ) : null}
    </Box>
  );
}

// Inclusive, so a single-day range reads 1 rather than 0.
function daysInRange(from, to) {
  const start = parseLocalDate(from);
  const end = parseLocalDate(to);
  if (!start || !end) return 0;
  return Math.max(0, Math.round((end - start) / 86400000) + 1);
}

// Same rule the week headers and the day rows use — a day is complete at 5 of 5 sections.
function incompleteCount(days) {
  return days.filter((day) => computeStepMeta(day).filter((step) => step.done).length < 5).length;
}

function groupByWeek(days) {
  const groups = new Map();
  days.forEach((day) => {
    const date = parseLocalDate(day.date);
    const monday = new Date(date);
    monday.setDate(date.getDate() - ((date.getDay() + 6) % 7));
    const key = toISODate(monday);
    if (!groups.has(key)) groups.set(key, { key, label: formatShort(key), days: [], incomplete: 0 });
    const group = groups.get(key);
    group.days.push(day);
    if (computeStepMeta(day).filter((step) => step.done).length < 5) group.incomplete += 1;
  });
  return [...groups.values()];
}

function formatShort(iso) {
  if (!iso) return null;
  // Two shapes reach this: a plain date (the range bounds) and a UTC instant (the last
  // export's generated_at). parseLocalDate only understands the first — it appends
  // T00:00:00, which turns an instant into an unparseable string and rendered "Invalid Date".
  const date = String(iso).length > 10 ? new Date(iso) : parseLocalDate(iso);
  return date.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}

function ExportButton({ onClick, label = "Export range", wide }) {
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      sx={{
        display: "inline-flex", alignItems: "center", gap: "8px", cursor: "pointer", border: "none",
        px: wide ? "22px" : "16px", py: wide ? "12px" : "10px", borderRadius: "9px", fontFamily: FONT_FAMILY,
        fontSize: "0.86rem", fontWeight: 600, background: `linear-gradient(135deg, ${AMBER}, #E0A020)`, color: "#0d1117",
      }}
    >
      <FileDownloadOutlinedIcon sx={{ fontSize: "1rem" }} />
      {label}
    </Box>
  );
}

function DayRow({ day, isLast, isToday, onContinue, onExportDay }) {
  const steps = computeStepMeta(day);
  const completedCount = steps.filter((s) => s.done).length;
  const complete = completedCount === steps.length;
  const date = parseLocalDate(day.date);
  // A reopened day still carries its sign-off time — that is the trail — so completed_at
  // alone would keep reading "signed" on a day that is open again.
  const timeOf = (value) => formatTimeOfDay(value);
  const signedOff = day.locked && day.completed_at ? `signed ${timeOf(day.completed_at)}` : null;
  const reopened = !day.locked && day.reopened_at ? `reopened ${timeOf(day.reopened_at)}` : null;

  return (
    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "72px 1fr", sm: "116px 1fr auto" }, gap: "12px", alignItems: "center", px: "16px", py: "14px", borderBottom: isLast ? "none" : "1px solid rgba(255,255,255,0.04)" }}>
      <Box>
        <Typography sx={{ color: "#fff", fontSize: "1rem", fontWeight: 700 }}>
          {date.toLocaleDateString(undefined, { day: "numeric", month: "short" })}
        </Typography>
        <Typography sx={{ color: "rgba(255,255,255,0.45)", fontSize: "0.72rem" }}>
          {date.toLocaleDateString(undefined, { weekday: "long" })}{isToday ? " · today" : ""}
        </Typography>
      </Box>

      <Box sx={{ minWidth: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
          <Box component="span" sx={{ color: complete ? OK_GREEN_TEXT : WARN_YELLOW, fontSize: "0.7rem" }}>●</Box>
          <Typography sx={{ fontSize: "0.82rem", color: "rgba(255,255,255,0.8)" }}>
            {completedCount} / {steps.length} sections complete
          </Typography>
          {!complete ? (
            <Box component="span" sx={{ fontSize: "0.66rem", fontWeight: 700, px: "8px", py: "3px", borderRadius: "4px", letterSpacing: "0.05em", textTransform: "uppercase", backgroundColor: "rgba(251,191,36,0.14)", color: WARN_YELLOW }}>
              {isToday ? "In progress" : "Incomplete"}
            </Box>
          ) : null}
        </Box>
        <Typography sx={{ color: "rgba(255,255,255,0.45)", fontSize: "0.74rem", mt: "3px" }}>
          {steps.map((step) => `${SHORT_NAMES[step.key]} ${step.done ? "✓" : "pending"}`).join(" · ")}
          {signedOff || reopened ? ` · ${signedOff || reopened}` : ""}
        </Typography>
      </Box>

      <Box
        component="button"
        type="button"
        onClick={isToday ? onContinue : onExportDay}
        sx={{ display: { xs: "none", sm: "block" }, background: "none", border: "none", p: 0, cursor: "pointer", fontFamily: FONT_FAMILY, fontSize: "0.78rem", fontWeight: 600, color: AMBER, whiteSpace: "nowrap" }}
      >
        {isToday ? "Continue →" : "View PDF →"}
      </Box>
    </Box>
  );
}
